import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft,
  TrendingUp,
  Calculator,
  CreditCard,
  Target,
  Zap,
  ChevronRight,
  Info,
  Plus,
  Minus,
  BarChart3,
  Percent,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  HelpCircle,
  Sliders,
  Building,
  PiggyBank,
  Wallet,
  X,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUREAU_COLORS = {
  EQUIFAX: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400" },
  EXPERIAN: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400" },
  TRANSUNION: { bg: "bg-green-500/10", border: "border-green-500/30", text: "text-green-400" }
};

export default function ScoreSimulatorEnhanced() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [results, setResults] = useState(null);
  
  // Bureau selection
  const [activeBureaus, setActiveBureaus] = useState({
    EQUIFAX: true,
    EXPERIAN: true,
    TRANSUNION: true
  });
  
  // Per-bureau scores and derogs
  const [bureauData, setBureauData] = useState({
    EQUIFAX: { current_score: 580, derogs: { collections_recent: 0, collections_old: 0, chargeoffs_recent: 0, chargeoffs_old: 0, late30_recent: 0, late60_recent: 0, late90_recent: 0 }},
    EXPERIAN: { current_score: 580, derogs: { collections_recent: 0, collections_old: 0, chargeoffs_recent: 0, chargeoffs_old: 0, late30_recent: 0, late60_recent: 0, late90_recent: 0 }},
    TRANSUNION: { current_score: 580, derogs: { collections_recent: 0, collections_old: 0, chargeoffs_recent: 0, chargeoffs_old: 0, late30_recent: 0, late60_recent: 0, late90_recent: 0 }}
  });
  
  // Credit cards for utilization
  const [creditCards, setCreditCards] = useState([]);
  
  // Other factors
  const [factors, setFactors] = useState({
    model: "BLEND",
    avg_age_years: null,
    oldest_age_years: null,
    revolving_accounts_count: null,
    installment_accounts_count: null,
    has_mortgage: false,
    has_auto: false,
    hard_inquiries_0_6mo: 0,
    hard_inquiries_7_12mo: 0,
    accounts_opened_0_6mo: 0,
    accounts_opened_7_12mo: 0,
    removal_percentage: 100
  });
  
  // AU Tradelines
  const [auTradelines, setAuTradelines] = useState([]);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    utilization: true,
    age: false,
    mix: false,
    inquiries: false,
    au: false
  });

  useEffect(() => {
    if (id) {
      fetchClient();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${id}`);
      setClient(response.data);
      
      // Set scores from client data
      const newBureauData = { ...bureauData };
      if (response.data.score_equifax) {
        newBureauData.EQUIFAX.current_score = response.data.score_equifax;
      }
      if (response.data.score_experian) {
        newBureauData.EXPERIAN.current_score = response.data.score_experian;
      }
      if (response.data.score_transunion) {
        newBureauData.TRANSUNION.current_score = response.data.score_transunion;
      }
      setBureauData(newBureauData);
      
    } catch (error) {
      console.error("Failed to fetch client:", error);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      // Build request payload
      const payload = {
        model: factors.model,
        removal_percentage: factors.removal_percentage,
        avg_age_years: factors.avg_age_years,
        oldest_age_years: factors.oldest_age_years,
        revolving_accounts_count: factors.revolving_accounts_count,
        installment_accounts_count: factors.installment_accounts_count,
        has_mortgage: factors.has_mortgage,
        has_auto: factors.has_auto,
        hard_inquiries_0_6mo: factors.hard_inquiries_0_6mo,
        hard_inquiries_7_12mo: factors.hard_inquiries_7_12mo,
        accounts_opened_0_6mo: factors.accounts_opened_0_6mo,
        accounts_opened_7_12mo: factors.accounts_opened_7_12mo
      };
      
      // Add active bureaus
      if (activeBureaus.EQUIFAX) {
        payload.equifax = bureauData.EQUIFAX;
      }
      if (activeBureaus.EXPERIAN) {
        payload.experian = bureauData.EXPERIAN;
      }
      if (activeBureaus.TRANSUNION) {
        payload.transunion = bureauData.TRANSUNION;
      }
      
      // Add credit cards if any
      if (creditCards.length > 0) {
        payload.credit_cards = creditCards;
      }
      
      // Add AU tradelines if any
      if (auTradelines.length > 0) {
        payload.au_tradelines = auTradelines;
      }
      
      const response = await axios.post(`${API}/score-simulator/calculate-enhanced`, payload);
      setResults(response.data);
      toast.success("Simulation complete!");
    } catch (error) {
      console.error("Simulation error:", error);
      toast.error("Failed to run simulation");
    } finally {
      setSimulating(false);
    }
  };

  const resetForm = () => {
    setBureauData({
      EQUIFAX: { current_score: 580, derogs: { collections_recent: 0, collections_old: 0, chargeoffs_recent: 0, chargeoffs_old: 0, late30_recent: 0, late60_recent: 0, late90_recent: 0 }},
      EXPERIAN: { current_score: 580, derogs: { collections_recent: 0, collections_old: 0, chargeoffs_recent: 0, chargeoffs_old: 0, late30_recent: 0, late60_recent: 0, late90_recent: 0 }},
      TRANSUNION: { current_score: 580, derogs: { collections_recent: 0, collections_old: 0, chargeoffs_recent: 0, chargeoffs_old: 0, late30_recent: 0, late60_recent: 0, late90_recent: 0 }}
    });
    setCreditCards([]);
    setAuTradelines([]);
    setFactors({
      model: "BLEND",
      avg_age_years: null,
      oldest_age_years: null,
      revolving_accounts_count: null,
      installment_accounts_count: null,
      has_mortgage: false,
      has_auto: false,
      hard_inquiries_0_6mo: 0,
      hard_inquiries_7_12mo: 0,
      accounts_opened_0_6mo: 0,
      accounts_opened_7_12mo: 0,
      removal_percentage: 100
    });
    setResults(null);
  };

  const updateBureauDerog = (bureau, field, value) => {
    setBureauData(prev => ({
      ...prev,
      [bureau]: {
        ...prev[bureau],
        derogs: {
          ...prev[bureau].derogs,
          [field]: Math.max(0, value)
        }
      }
    }));
  };

  const updateBureauScore = (bureau, score) => {
    setBureauData(prev => ({
      ...prev,
      [bureau]: {
        ...prev[bureau],
        current_score: Math.min(850, Math.max(300, score))
      }
    }));
  };

  const addCreditCard = () => {
    setCreditCards([...creditCards, { name: "", limit: 0, balance_current: 0, balance_target: 0 }]);
  };

  const updateCreditCard = (index, field, value) => {
    const updated = [...creditCards];
    updated[index][field] = field === "name" ? value : Math.max(0, parseInt(value) || 0);
    setCreditCards(updated);
  };

  const removeCreditCard = (index) => {
    setCreditCards(creditCards.filter((_, i) => i !== index));
  };

  const addAuTradeline = () => {
    setAuTradelines([...auTradelines, { age_years: 5, limit: 10000, utilization: 5, is_clean: true }]);
  };

  const updateAuTradeline = (index, field, value) => {
    const updated = [...auTradelines];
    if (field === "is_clean") {
      updated[index][field] = value;
    } else {
      updated[index][field] = Math.max(0, parseFloat(value) || 0);
    }
    setAuTradelines(updated);
  };

  const removeAuTradeline = (index) => {
    setAuTradelines(auTradelines.filter((_, i) => i !== index));
  };

  const getScoreColor = (score) => {
    if (score >= 800) return "text-green-400";
    if (score >= 740) return "text-emerald-400";
    if (score >= 670) return "text-blue-400";
    if (score >= 580) return "text-amber-400";
    return "text-red-400";
  };

  const getScoreBgColor = (score) => {
    if (score >= 800) return "bg-green-500/20 border-green-500/30";
    if (score >= 740) return "bg-emerald-500/20 border-emerald-500/30";
    if (score >= 670) return "bg-blue-500/20 border-blue-500/30";
    if (score >= 580) return "bg-amber-500/20 border-amber-500/30";
    return "bg-red-500/20 border-red-500/30";
  };

  const CounterInput = ({ value, onChange, label, min = 0, max = 20 }) => (
    <div className="flex items-center gap-2">
      <Button size="icon" variant="outline" className="h-7 w-7 border-zinc-600" onClick={() => onChange(value - 1)} disabled={value <= min}>
        <Minus className="w-3 h-3" />
      </Button>
      <span className="w-6 text-center font-mono text-sm">{value}</span>
      <Button size="icon" variant="outline" className="h-7 w-7 border-zinc-600" onClick={() => onChange(value + 1)} disabled={value >= max}>
        <Plus className="w-3 h-3" />
      </Button>
      <span className="text-xs text-zinc-400 ml-1">{label}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="score-simulator-enhanced" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {id && (
          <Link to={`/clients/${id}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
              <Calculator className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Score Simulator</h1>
              <p className="text-muted-foreground text-sm">
                {client ? `${client.name}'s` : 'Multi-Bureau'} Full Factor Analysis
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={factors.model} onValueChange={(v) => setFactors({ ...factors, model: v })}>
            <SelectTrigger className="w-36 bg-zinc-800 border-zinc-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BLEND">Blend</SelectItem>
              <SelectItem value="FICO8">FICO 8</SelectItem>
              <SelectItem value="VANTAGE3">VantageScore 3</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={resetForm} className="border-zinc-600">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Column - Bureau Inputs */}
        <div className="xl:col-span-2 space-y-4">
          
          {/* Bureau Cards */}
          {["EQUIFAX", "EXPERIAN", "TRANSUNION"].map((bureau) => (
            <Card key={bureau} className={`border-zinc-700 ${activeBureaus[bureau] ? BUREAU_COLORS[bureau].bg : 'bg-zinc-900/30 opacity-60'}`}>
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={activeBureaus[bureau]}
                      onCheckedChange={(v) => setActiveBureaus({ ...activeBureaus, [bureau]: v })}
                    />
                    <CardTitle className={`text-base ${BUREAU_COLORS[bureau].text}`}>
                      {bureau}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-400">Score:</span>
                    <Input
                      type="number"
                      min={300}
                      max={850}
                      value={bureauData[bureau].current_score}
                      onChange={(e) => updateBureauScore(bureau, parseInt(e.target.value) || 300)}
                      className="w-20 h-8 bg-zinc-800 border-zinc-600 text-center font-bold"
                      disabled={!activeBureaus[bureau]}
                    />
                  </div>
                </div>
              </CardHeader>
              {activeBureaus[bureau] && (
                <CardContent className="pt-0 space-y-3">
                  {/* Collections Row */}
                  <div className="grid grid-cols-2 gap-4 p-3 bg-zinc-800/50 rounded-lg">
                    <div className="space-y-2">
                      <div className="text-xs text-red-400 font-medium">Collections</div>
                      <CounterInput
                        value={bureauData[bureau].derogs.collections_recent}
                        onChange={(v) => updateBureauDerog(bureau, "collections_recent", v)}
                        label="Recent"
                      />
                      <CounterInput
                        value={bureauData[bureau].derogs.collections_old}
                        onChange={(v) => updateBureauDerog(bureau, "collections_old", v)}
                        label="Old (25+mo)"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="text-xs text-orange-400 font-medium">Charge-offs</div>
                      <CounterInput
                        value={bureauData[bureau].derogs.chargeoffs_recent}
                        onChange={(v) => updateBureauDerog(bureau, "chargeoffs_recent", v)}
                        label="Recent"
                      />
                      <CounterInput
                        value={bureauData[bureau].derogs.chargeoffs_old}
                        onChange={(v) => updateBureauDerog(bureau, "chargeoffs_old", v)}
                        label="Old (25+mo)"
                      />
                    </div>
                  </div>
                  
                  {/* Late Payments Row */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <div className="text-xs text-yellow-400 font-medium mb-2">Late Payments (Recent)</div>
                    <div className="grid grid-cols-3 gap-2">
                      <CounterInput
                        value={bureauData[bureau].derogs.late30_recent}
                        onChange={(v) => updateBureauDerog(bureau, "late30_recent", v)}
                        label="30d"
                      />
                      <CounterInput
                        value={bureauData[bureau].derogs.late60_recent}
                        onChange={(v) => updateBureauDerog(bureau, "late60_recent", v)}
                        label="60d"
                      />
                      <CounterInput
                        value={bureauData[bureau].derogs.late90_recent}
                        onChange={(v) => updateBureauDerog(bureau, "late90_recent", v)}
                        label="90+d"
                      />
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}

          {/* Credit Cards for Utilization */}
          <Collapsible open={expandedSections.utilization} onOpenChange={(v) => setExpandedSections({ ...expandedSections, utilization: v })}>
            <Card className="border-zinc-700 bg-zinc-900/50">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="py-3 cursor-pointer hover:bg-zinc-800/50">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-cyan-400" />
                      Credit Card Utilization
                      <Badge variant="outline" className="ml-2">{creditCards.length} cards</Badge>
                    </CardTitle>
                    {expandedSections.utilization ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-3">
                  {creditCards.map((card, idx) => (
                    <div key={idx} className="grid grid-cols-5 gap-2 p-3 bg-zinc-800/50 rounded-lg items-end">
                      <div className="space-y-1">
                        <Label className="text-xs">Card Name</Label>
                        <Input
                          placeholder="Chase"
                          value={card.name}
                          onChange={(e) => updateCreditCard(idx, "name", e.target.value)}
                          className="h-8 bg-zinc-700 border-zinc-600 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Limit</Label>
                        <Input
                          type="number"
                          placeholder="5000"
                          value={card.limit || ""}
                          onChange={(e) => updateCreditCard(idx, "limit", e.target.value)}
                          className="h-8 bg-zinc-700 border-zinc-600 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Current Bal</Label>
                        <Input
                          type="number"
                          placeholder="4500"
                          value={card.balance_current || ""}
                          onChange={(e) => updateCreditCard(idx, "balance_current", e.target.value)}
                          className="h-8 bg-zinc-700 border-zinc-600 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Target Bal</Label>
                        <Input
                          type="number"
                          placeholder="500"
                          value={card.balance_target || ""}
                          onChange={(e) => updateCreditCard(idx, "balance_target", e.target.value)}
                          className="h-8 bg-zinc-700 border-zinc-600 text-sm"
                        />
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => removeCreditCard(idx)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addCreditCard} className="w-full border-dashed">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Credit Card
                  </Button>
                  {creditCards.length > 0 && (
                    <div className="text-xs text-zinc-400 p-2 bg-cyan-500/10 rounded">
                      <strong>Tip:</strong> Enter your current balance and what you plan to pay it down to. Utilization drops = big score gains!
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Additional Factors */}
          <div className="grid grid-cols-2 gap-4">
            {/* Credit Age */}
            <Collapsible open={expandedSections.age} onOpenChange={(v) => setExpandedSections({ ...expandedSections, age: v })}>
              <Card className="border-zinc-700 bg-zinc-900/50">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-zinc-800/50">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-purple-400" />
                      Credit Age
                      {expandedSections.age ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Avg Age (years)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="3.5"
                        value={factors.avg_age_years || ""}
                        onChange={(e) => setFactors({ ...factors, avg_age_years: e.target.value ? parseFloat(e.target.value) : null })}
                        className="h-8 bg-zinc-800 border-zinc-600"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Oldest Account (years)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="7"
                        value={factors.oldest_age_years || ""}
                        onChange={(e) => setFactors({ ...factors, oldest_age_years: e.target.value ? parseFloat(e.target.value) : null })}
                        className="h-8 bg-zinc-800 border-zinc-600"
                      />
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Credit Mix */}
            <Collapsible open={expandedSections.mix} onOpenChange={(v) => setExpandedSections({ ...expandedSections, mix: v })}>
              <Card className="border-zinc-700 bg-zinc-900/50">
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="py-3 cursor-pointer hover:bg-zinc-800/50">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-emerald-400" />
                      Credit Mix
                      {expandedSections.mix ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Revolving</Label>
                        <Input
                          type="number"
                          placeholder="3"
                          value={factors.revolving_accounts_count || ""}
                          onChange={(e) => setFactors({ ...factors, revolving_accounts_count: e.target.value ? parseInt(e.target.value) : null })}
                          className="h-8 bg-zinc-800 border-zinc-600"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Installment</Label>
                        <Input
                          type="number"
                          placeholder="2"
                          value={factors.installment_accounts_count || ""}
                          onChange={(e) => setFactors({ ...factors, installment_accounts_count: e.target.value ? parseInt(e.target.value) : null })}
                          className="h-8 bg-zinc-800 border-zinc-600"
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-xs">
                        <Switch checked={factors.has_mortgage} onCheckedChange={(v) => setFactors({ ...factors, has_mortgage: v })} />
                        Mortgage
                      </label>
                      <label className="flex items-center gap-2 text-xs">
                        <Switch checked={factors.has_auto} onCheckedChange={(v) => setFactors({ ...factors, has_auto: v })} />
                        Auto Loan
                      </label>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>

          {/* Inquiries */}
          <Collapsible open={expandedSections.inquiries} onOpenChange={(v) => setExpandedSections({ ...expandedSections, inquiries: v })}>
            <Card className="border-zinc-700 bg-zinc-900/50">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="py-3 cursor-pointer hover:bg-zinc-800/50">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    Inquiries & New Credit
                    {expandedSections.inquiries ? <ChevronUp className="w-3 h-3 ml-auto" /> : <ChevronDown className="w-3 h-3 ml-auto" />}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Hard Inq (0-6mo)</Label>
                      <Input type="number" value={factors.hard_inquiries_0_6mo} onChange={(e) => setFactors({ ...factors, hard_inquiries_0_6mo: parseInt(e.target.value) || 0 })} className="h-8 bg-zinc-800 border-zinc-600" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Hard Inq (7-12mo)</Label>
                      <Input type="number" value={factors.hard_inquiries_7_12mo} onChange={(e) => setFactors({ ...factors, hard_inquiries_7_12mo: parseInt(e.target.value) || 0 })} className="h-8 bg-zinc-800 border-zinc-600" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">New Accts (0-6mo)</Label>
                      <Input type="number" value={factors.accounts_opened_0_6mo} onChange={(e) => setFactors({ ...factors, accounts_opened_0_6mo: parseInt(e.target.value) || 0 })} className="h-8 bg-zinc-800 border-zinc-600" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">New Accts (7-12mo)</Label>
                      <Input type="number" value={factors.accounts_opened_7_12mo} onChange={(e) => setFactors({ ...factors, accounts_opened_7_12mo: parseInt(e.target.value) || 0 })} className="h-8 bg-zinc-800 border-zinc-600" />
                    </div>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Removal Slider & Run Button */}
          <Card className="border-zinc-700 bg-gradient-to-r from-purple-500/10 to-pink-500/10">
            <CardContent className="py-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-400">Items to Remove</span>
                <span className="text-2xl font-bold text-purple-400">{factors.removal_percentage}%</span>
              </div>
              <Slider
                value={[factors.removal_percentage]}
                onValueChange={([v]) => setFactors({ ...factors, removal_percentage: v })}
                max={100}
                step={10}
              />
              <Button 
                onClick={runSimulation} 
                disabled={simulating || !Object.values(activeBureaus).some(v => v)}
                className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {simulating ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Run Simulation
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Results */}
        <div className="space-y-4">
          {results ? (
            <>
              {Object.entries(results.bureaus).map(([bureau, r]) => (
                <Card key={bureau} className={`border ${BUREAU_COLORS[bureau]?.border || 'border-zinc-600'} ${BUREAU_COLORS[bureau]?.bg || ''}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className={`text-lg ${BUREAU_COLORS[bureau]?.text || ''}`}>{bureau}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <div className="text-xs text-zinc-500">Current</div>
                        <div className={`text-2xl font-bold ${getScoreColor(r.current_score)}`}>{r.current_score}</div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-green-400" />
                      <div className="text-center">
                        <div className="text-xs text-zinc-500">Projected</div>
                        <div className={`text-2xl font-bold ${getScoreColor(r.projected_expected)}`}>{r.projected_expected}</div>
                      </div>
                    </div>
                    <div className="text-center">
                      <Badge className={`${getScoreBgColor(r.projected_expected)}`}>
                        +{r.delta_expected} points → {r.projected_tier}
                      </Badge>
                    </div>
                    <div className="text-xs text-zinc-500 flex justify-between">
                      <span>Low: {r.projected_low} (+{r.delta_low})</span>
                      <span>High: {r.projected_high} (+{r.delta_high})</span>
                    </div>
                    
                    {/* Driver breakdown */}
                    <div className="pt-2 border-t border-zinc-700 space-y-1">
                      {r.drivers.filter(d => (d.delta || d.delta_mid) !== 0).map((d, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="text-zinc-400">{d.factor}</span>
                          <span className={d.delta >= 0 || d.delta_mid >= 0 ? "text-green-400" : "text-red-400"}>
                            {d.delta !== undefined ? (d.delta >= 0 ? `+${d.delta}` : d.delta) : `+${d.delta_low}-${d.delta_high}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 text-xs text-zinc-500">
                <Info className="w-4 h-4 inline mr-1" />
                {results.disclaimer}
              </div>
            </>
          ) : (
            <Card className="border-zinc-700 bg-zinc-900/50">
              <CardContent className="py-12 text-center">
                <Calculator className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">
                  Enter derogatory counts per bureau and click "Run Simulation"
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
